/**
 * PortfolioAwareAdvisor — the one Advisor component every Premium page
 * should mount. It wires the existing `AIPropertyChat` + provider so a
 * page only supplies:
 *
 *   - `contextType`      : one of "portfolio" | "portfolio_doctor" |
 *                          "stress_test" | "rent_optimizer" (matches the
 *                          backend registry keys in
 *                          services/portfolio_advisor_contexts.py)
 *   - `context`          : compact page-state dict — RendPleX engine
 *                          values only, NEVER raw form inputs.
 *   - `suggestedQuestions`: optional string[] of quick-tap prompts
 *                          shown in the empty state.
 *
 * The backend enforces `ai_advisor_portfolio` capability, so free/Pro
 * users cannot exfiltrate portfolio data by mounting this manually.
 *
 * Adding a new Premium tool = mount `<PortfolioAwareAdvisor>` with the
 * appropriate contextType. No changes to AIPropertyChat needed.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import AIPropertyChat from './AIPropertyChat';
import { PropertyAdvisorProvider } from '../context/PropertyAdvisorContext';

const CONTEXT_LABEL_KEYS = {
  portfolio: 'aiAdvisor.contexts.portfolio',
  portfolio_doctor: 'aiAdvisor.contexts.portfolio_doctor',
  stress_test: 'aiAdvisor.contexts.stress_test',
  rent_optimizer: 'aiAdvisor.contexts.rent_optimizer',
};

const DEFAULT_QUESTION_KEYS = {
  portfolio: 'aiAdvisor.contexts.portfolioQuestions',
  portfolio_doctor: 'aiAdvisor.contexts.portfolioDoctorQuestions',
  stress_test: 'aiAdvisor.contexts.stressTestQuestions',
  rent_optimizer: 'aiAdvisor.contexts.rentOptimizerQuestions',
};

export function PortfolioAwareAdvisor({
  contextType,
  context = {},
  suggestedQuestions,
  propertyContext,
}) {
  const { t } = useTranslation();
  const labelKey = CONTEXT_LABEL_KEYS[contextType];
  const questionsKey = DEFAULT_QUESTION_KEYS[contextType];
  // `returnObjects: true` lets us pull the array from i18n JSON.
  const resolvedQuestions =
    suggestedQuestions ||
    (questionsKey ? t(questionsKey, { returnObjects: true, defaultValue: [] }) : []);

  // The provider carries all the metadata AIPropertyChat needs. Pages pass
  // pure engine values via `context`; we never re-derive numbers here.
  const value = {
    ...(propertyContext || {}),
    __contextType: contextType,
    __contextLabel: labelKey ? t(labelKey) : null,
    __pageContext: context,
    __suggestedQuestions: Array.isArray(resolvedQuestions) ? resolvedQuestions : [],
  };

  return (
    <PropertyAdvisorProvider value={value}>
      <AIPropertyChat />
    </PropertyAdvisorProvider>
  );
}

export default PortfolioAwareAdvisor;
